// MUI-free copy of Ui-components/tokenize-help.
import React, {memo} from 'react';
import type {StaticImageData} from 'next/image';
import parse from 'html-react-parser';
import Container from '@/components/atoms/Container';
import IconCard from '@/components/molecules/IconCard';
import CheckList from '@/components/atoms/CheckList';
import FadeInUp from '@/components/atoms/FadeInUp';
import OptimizedImage from '@/components/atoms/OptimizedImage';
import {ICON_TILE_ICON_SIZE} from '@/styles/iconTile';
import styles from './TokenizeHelp.module.scss';

export interface TokenizeHelpItem {
    heading: string;
    /** Icon image shown in the gradient tile. */
    src?: string | StaticImageData;
    /** Makes the heading a link. */
    redirect?: string;
    /** HTML string. */
    body?: string;
    /** HTML strings, rendered as a checklist under the body. */
    content?: string[];
    /** Pill tags at the bottom of the card. */
    tags?: string[];
}

interface TokenizeHelpProps {
    heading?: string;
    /** HTML string. */
    subheading?: string;
    contentData?: TokenizeHelpItem[];
}

const TokenizeHelpCard = ({item}: {item: TokenizeHelpItem}) => (
    // home/common-card (no blueHover): outer card + inner white-ring box.
    <IconCard
        layout="inline"
        heading={item.heading}
        href={item.redirect}
        icon={
            item.src ? (
                <OptimizedImage
                    alt={item.heading}
                    src={item.src}
                    className="home-service-icon"
                    width={ICON_TILE_ICON_SIZE}
                    height={ICON_TILE_ICON_SIZE}
                />
            ) : undefined
        }
    >
        <p className={`font-regular-text remove-underline ${styles.text}`}>{item.body ? parse(item.body) : ''}</p>
        <CheckList items={item.content} textClassName={`generative-transforming-subheading ${styles.text}`} />
        {item.tags && item.tags.length > 0 && (
            <div className={styles.tags}>
                {item.tags.map((tag) => (
                    <div key={tag} className={styles.tag}>
                        <p className={`font-headers ${styles.tagText}`}>{tag}</p>
                    </div>
                ))}
            </div>
        )}
    </IconCard>
);

const TokenizeHelp = ({heading, subheading, contentData}: TokenizeHelpProps) => (
    <div>
        <Container>
            <div className={styles.header}>
                <h2 className={`font-headers ${styles.heading}`}>{heading}</h2>
                <p className={`font-regular-text generative-transforming-subheading ${styles.subheading}`}>
                    {subheading ? parse(subheading) : ''}
                </p>
            </div>
            <div>
                <div className={styles.grid}>
                    {contentData?.map((item, index) => (
                        <div className={styles.item} key={index + 1}>
                            <FadeInUp className="height-full">
                                <TokenizeHelpCard item={item} />
                            </FadeInUp>
                        </div>
                    ))}
                </div>
            </div>
        </Container>
    </div>
);

export default memo(TokenizeHelp);
